<template>
  <section
    id="checkoutAddress"
    :class="[openClass, 'expandable_section']"
  >
    <h2
      class="expandable_header"
      @click="proceed(sectionName)"
    >
      {{ number }}. {{ $t('checkout.address.billingAndShipping') }}
    </h2>

    <span
      class="header-count"
      @click="proceed(sectionName)"
    />

    <div class="expandable_content">
      <div
        v-if="selected"
        class="checkout-address"
      >
        <p class="address-disclaimer">
          {{ $t('checkout.address.disclaimer') }}
        </p>
        <div
          v-if="guestAccount == 'true'"
          id="checkoutEmail"
          class="required-field"
        >
          <input
            id="checkout_email_address"
            v-model="emailAddress"
            v-validate="'required'"
            name="email_address"
            type="email"
            placeholder="*Email Address"
            data-vv-validate-on="input"
            :class="{ 'required': errors.has('email_address') }"
          >
          <label
            v-if="querying"
            class="small-label loading"
          >
            <span class="loading-icon" />
            <em>...validating email</em>
          </label>
          <span
            v-show="errors.has('email_address')"
            class="error"
          >
            {{ errors.first('email_address') }}
          </span>

          <CheckoutLogin
            v-if="existingEmailAccount"
            :email="emailAddress"
          />
        </div>
        <div class="required-field">
          <input
            id="checkout_first_name"
            v-model="firstName"
            v-validate="'required'"
            name="first_name"
            type="text"
            :placeholder="$t('checkout.address.firstName')"
            :class="{ 'required': errors.has('first_name') }"
          >
          <span
            v-show="errors.has('first_name')"
            class="error"
          >
            {{ errors.first('first_name') }}
          </span>
        </div>

        <div class="required-field">
          <input
            id="checkout_last_name"
            v-model="lastName"
            v-validate="'required'"
            name="last_name"
            type="text"
            :placeholder="$t('checkout.address.lastName')"
            :class="{ 'required': errors.has('last_name') }"
          >
          <span
            v-show="errors.has('last_name')"
            class="error"
          >
            {{ errors.first('last_name') }}
          </span>
        </div>

        <div class="corporate-checkbox">
          <input
            id="checkout-address-corporate"
            v-model="corporate"
            type="checkbox"
          >
          <label for="checkout-address-corporate">{{ $t('checkout.address.corporate') }}</label>
        </div>

        <div class="required-field">
          <input
            id="checkout_phone_number"
            v-model="phoneNumber"
            v-validate="'required'"
            name="phone_number"
            type="text"
            :placeholder="$t('checkout.address.phoneNumber')"
            :class="{ 'required': errors.has('phone_number') }"
          >
          <span
            v-show="errors.has('phone_number')"
            class="error"
          >
            {{ errors.first('phone_number') }}
          </span>
        </div>

        <div
          v-if="corporate"
          class="corporate-fields"
        >
          <input
            v-model="companyName"
            type="text"
            autocomplete="off"
            :placeholder="$t('checkout.address.companyName')"
          >

          <input
            v-model="purchaseOrderNumber"
            type="text"
            autocomplete="off"
            :placeholder="$t('checkout.address.purchaseOrderNumber')"
          >
        </div>

        <div class="required-field">
          <input
            id="checkout_address"
            v-model="address1"
            v-validate="'required'"
            name="address1"
            type="text"
            :placeholder="$t('checkout.address.address1')"
            :class="{ 'required': errors.has('address1') }"
          >
          <span
            v-show="errors.has('address1')"
            class="error"
          >
            {{ errors.first('address1') }}
          </span>
        </div>

        <div>
          <input
            v-model="address2"
            type="text"
            :placeholder="$t('checkout.address.address2')"
          >
        </div>

        <div class="required-field">
          <input
            id="checkout_city"
            v-model="city"
            v-validate="'required'"
            name="city"
            type="text"
            :placeholder="$t('checkout.address.city')"
            :class="{ 'required': errors.has('city') }"
          >
          <span
            v-show="errors.has('city')"
            class="error"
          >
            {{ errors.first('city') }}
          </span>
        </div>

        <div class="required-field">
          <input
            id="checkout_zip_code"
            v-model="zipCode"
            v-validate="'required'"
            name="zip_code"
            type="text"
            :placeholder="$t('checkout.address.zipCode')"
            :class="{ 'required': errors.has('zip_code') }"
          >

          <span
            v-show="errors.has('zip_code')"
            class="error"
          >
            {{ errors.first('zip_code') }}
          </span>
        </div>
        <div class="required-field">
          <select
            id="checkout_country"
            v-model="country"
            v-validate="{ required: true, excluded: blockedCountries }"
            name="country"
            :class="{ 'required': errors.has('country') }"
            @change="updateCountryAndState"
          >
            <option
              :value="null"
              disabled
            >
              {{ $t('checkout.address.country') }}
            </option>

            <option
              v-for="code in preferredCountriesList"
              :key="code.code"
              :value="code.code"
            >
              {{ code.translations[currentLocale] || code.name }}
            </option>
            <hr>

            <option
              v-for="selectedCountry in countriesList"
              :key="selectedCountry.code"
              :value="selectedCountry.code"
            >
              {{ selectedCountry.translations[currentLocale] || selectedCountry.name }}
            </option>
          </select>
          <span
            v-show="errors.has('country')"
            class="error"
          >
            {{ errors.first('country') }}
          </span>
        </div>

        <div class="required-field">
          <select
            v-if="stateLists.length > 0"
            id="checkout_state"
            v-model="state"
            v-validate="{ is_not: 'missing state value' }"
            name="state"
            :class="{ 'required': errors.has('state') }"
          >
            <option
              value="missing state value"
              disabled
            >
              {{ $t('checkout.address.state') }}
            </option>
            <option
              v-for="selectedState in stateLists"
              :key="selectedState.code"
              :value="selectedState.code"
            >
              {{ selectedState.name }}
            </option>
          </select>
          <span
            v-show="errors.has('state')"
            class="error"
          >
            {{ errors.first('state') }}
          </span>
        </div>
      </div>

      <button
        class="continue-button btn btn-primary"
        :disabled="!validated"
        @click="checkRequiredFields"
      >
        {{ $t('cookie.continue') }}
      </button>
    </div>
  </section>
</template>

<script>
/* global locale */

import CountryData from '../country_data'
import CartApi from '../cart/cart_api'
import CheckoutLogin from './checkout_login'
import FetchCall from '../local_fetch_call'
import { mapMutations, mapState, mapActions } from 'vuex'

export default {
  components: {
    CheckoutLogin
  },

  props: ['number'],

  data() {
    return {
      validated: false,
      querying: false,
      emailAddress: '',
      existingEmailAccount: false,
      countriesList: [],
      sectionName: 'address',
      showRequired: false,
      stateLists: [],
      preferredCountry: 'US',
      defaultLocale: '',
      preferredCountries: [],
      preferredCountriesList: []
    }
  },
  computed: {
    ...mapState('checkout', {
      billing: state => state.billing,
      hotelInCart: state => state.hotelInCart,
      isGuest: state => state.isGuest,
    }),
    address1: {
      get() {
        return this.billing.address1
      },
      set(value) {
        this.updateAddress1(value)
      }
    },
    address2: {
      get() {
        return this.billing.address2
      },
      set(value) {
        this.updateAddress2(value)
      }
    },
    city: {
      get() {
        return this.billing.city
      },
      set(value) {
        this.updateCity(value)
      }
    },
    companyName: {
      get() {
        return this.billing.companyName
      },
      set(value) {
        this.updateCompanyName(value)
      }
    },
    corporate: {
      get() {
        return this.billing.corporate
      },
      set(value) {
        this.updateCorporate(value)
      }
    },
    country: {
      get() {
        return this.billing.country
      },
      set(value) {
        this.updateCountry(value)
      }
    },
    firstName: {
      get() {
        return this.billing.firstName
      },
      set(value) {
        this.updateFirstName(value)
      }
    },
    lastName: {
      get() {
        return this.billing.lastName
      },
      set(value) {
        this.updateLastName(value)
      }
    },
    phoneNumber: {
      get() {
        return this.billing.phoneNumber
      },
      set(value) {
        this.updatePhoneNumber(value)
      }
    },
    purchaseOrderNumber: {
      get() {
        return this.billing.purchaseOrderNumber
      },
      set(value) {
        this.updatePurchaseOrderNumber(value)
      }
    },
    state: {
      get() {
        return this.billing.state
      },
      set(value) {
        this.updateState(value)
      }
    },
    zipCode: {
      get() {
        return this.billing.zipCode
      },
      set(value) {
        this.updateZipCode(value)
      }
    },
    guestAccount: {
      get() {
        return this.isGuest
      },
      set() {
        let el = document.querySelector('#accountStatus').dataset.guest
        this.updateIsGuest(el)
      }
    },

    blockedCountries() {
      return CountryData.blockedCountries().concat(['', null, undefined]).toString()
    },

    currentLocale() {
      return locale.toLowerCase()
    },

    selected() {
      return this.$store.state.checkout.section == this.sectionName
    },

    openClass() {
      return (this.selected) ? 'open' : ''
    },
  },

  watch: {
    country() {
      this.updateCountryAndState()
    },
    emailAddress: function(val) {
      this.updateEmailAddress(val)
      this.isExistingEmail(val)
    }
  },

  mounted() {
    let el = document.querySelector('#accountStatus').dataset.guest
    this.updateIsGuest(el)
    this.isValidated()
  },

  async created() {
    this.countriesList = CountryData.countryNamesAndCodes()
    await this.getOrder()
  },

  methods: {
    ...mapMutations('checkout', [
      'updatePaymentFields',
      'updateIsGuest',
      'updateAddress1',
      'updateAddress2',
      'updateEmailAddress',
      'updateCity',
      'updateCompanyName',
      'updateCorporate',
      'updateCountry',
      'updateFirstName',
      'updateLastName',
      'updatePhoneNumber',
      'updatePurchaseOrderNumber',
      'updateState',
      'updateZipCode'
    ]),
    ...mapActions('checkout', ['updateSection']),
    async getOrder() {
      let order = await CartApi.getOrder()

      this.firstName = order.first_name
      this.lastName = order.last_name
      this.corporate = order.corporate_group
      this.phoneNumber = order.phone
      this.address1 = order.street_1
      this.address2 = order.street_2
      this.city = order.city
      this.state = order.state
      this.country = order.country
      this.zipCode = order.zip_code

      if (order.items.length > 0) {
        if(order.items[0].preferred_country) this.preferredCountry = order.items[0].preferred_country
        this.defaultLocale = order.items[0].site_default_locale
        if (this.defaultLocale == 'en-GB'){
          this.preferredCountries = ['GB', 'US']
        } else {
          this.preferredCountries = ['US']
        }
        if (this.preferredCountry != 'US') this.preferredCountries.push(this.preferredCountry)
      }

      let that = this
      let tempCountriesList = { ...that.countriesList }
      Object.keys(this.countriesList).forEach(function(key){
        if(that.preferredCountries.includes(that.countriesList[key].code)){
          delete that.countriesList[key]
        } else {
          delete tempCountriesList[key]
        }
      })
      this.preferredCountries.forEach(function(key){
        that.preferredCountriesList.push(tempCountriesList[key])
      })

    },
    checkRequiredFields() {
      this.$validator.validate().then(valid => {
        if (valid) {
          let sectionNext = this.hotelInCart ? 'hotel' : 'review'
          this.proceed(sectionNext)
        } else {
          // Don't continue with invalid fields
          for (let field of this.$validator.fields) { field.update({ events: 'input|blur' }) }
        }
      })
    },
    updateCountryAndState() {
      CountryData.countryStates(this.country).then(states => {
        this.stateLists = (states == undefined ? [] : states)

        if (this.stateLists.length > 0) {
          this.state = this.state ? this.state : 'missing state value'
        } else {
          this.state = null
        }

        this.$emit('payment-fields-update', { country: this.country, state: this.state })
        this.updatePaymentFields({ country: this.country, state: this.state })
      })
    },
    proceed(section) {
      let body = JSON.stringify({
        order: {
          email_address: this.emailAddress,
          first_name: this.firstName,
          last_name: this.lastName,
          corporate_group: this.corporate,
          phone: this.phoneNumber,
          street_1: this.address1,
          street_2: this.address2,
          city: this.city,
          state: this.state,
          country: this.country,
          zip_code: this.zipCode
        }
      })

      FetchCall.fetchWithoutLocale(
        '/cart',
        'PUT',
        body
      )

      this.updateSection(section)
    },
    apiUserEmail(method, body) {
      this.validating = true
      let tok = document.getElementsByTagName('meta')['csrf-token']
      if (tok) { // csrf-token is null in testing
        tok = tok.getAttribute('content')
      }
      fetch('/customers/customer_email_validations', {
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': tok
        },
        method: method,
        body: body
      }).then((response) => {
        if(response.status == 200) {
          this.existingEmailAccount = true
          this.validated = false
        }
        else {
          this.existingEmailAccount = false
          this.validated = true
        }
      })
    },
    queryEmailAddress(){
      this.$validator.validate('email_address').then(valid => {
        if (valid) {
          this.apiUserEmail('POST', JSON.stringify({
            customer: {
              email: this.emailAddress
            }
          }))
        } else {
          this.existingEmailAccount = false
        }
      })
    },
    isExistingEmail() {
      if(!this.querying) {
        this.querying = true
        setTimeout(() => {
          this.queryEmailAddress()
          this.querying = false
        }, 1200)
      }
    },
    isValidated() {
      if(this.isGuest == 'false') {
        this.validated = true
      }
    },
  }
}
</script>
